/**
 * Shared constants for the PLATFORM tour — the cross-page walkthrough
 * that runs AFTER the curated replay + alert scene. Named "platform"
 * to distinguish it from the `onboarding-constants.ts` file, which
 * governs the earlier replay+alert scenes.
 *
 * The platform tour walks an authenticated/anon user through the four
 * big pieces of the app they haven't seen yet, in order:
 *
 *   Step 1 — /automations          Pre-made alerts (Xynth + community)
 *   Step 2 — /community            Public research sessions
 *   Step 3 — /trades (→ /blog)     Real trade ideas / case studies
 *   Step 4 — /pricing              Conversion — spotlight Start Free Trial
 *
 * Each step is a self-contained scene: the page reads the
 * `?ptour=<step>` query param on mount, runs its local
 * `useTour().start([...])` sequence, and on completion navigates to the
 * next step's URL (which carries `?ptour=<nextStep>`). The final
 * pricing step ends by spotlighting the "Start Free Trial" CTA — that's
 * where the tour dissolves into the conversion funnel.
 *
 * Why a URL query param (not a global zustand store):
 *   • Survives Next.js route transitions without a provider rehydration
 *     dance. Each new page just reads its own searchParams.
 *   • Shareable — if someone copies a /community?ptour=community URL
 *     they can drop into that step.
 *   • Dies naturally on tab close; no localStorage cleanup needed.
 *   • Gets stripped by each scene component AFTER the local tour
 *     finishes, so a browser-back or refresh doesn't re-fire.
 */

export const PLATFORM_TOUR_QUERY_PARAM = "ptour";

// ── Step values ──────────────────────────────────────────────────────────────
// String constants (not an enum) so they inline into URLs cleanly and
// match against searchParams.get() without coercion.

export const PLATFORM_TOUR_STEP_AUTOMATIONS = "automations";
export const PLATFORM_TOUR_STEP_COMMUNITY = "community";
export const PLATFORM_TOUR_STEP_TRADES = "trades";
export const PLATFORM_TOUR_STEP_PRICING = "pricing";

export type PlatformTourStep =
  | typeof PLATFORM_TOUR_STEP_AUTOMATIONS
  | typeof PLATFORM_TOUR_STEP_COMMUNITY
  | typeof PLATFORM_TOUR_STEP_TRADES
  | typeof PLATFORM_TOUR_STEP_PRICING;

/** Canonical step order. Each scene's onComplete handler uses this to
 *  look up the next step + navigate. Keeping it in one place avoids
 *  the "automations points at community, community points at trades"
 *  pointer chain being scattered across four files. */
export const PLATFORM_TOUR_ORDER: PlatformTourStep[] = [
  PLATFORM_TOUR_STEP_AUTOMATIONS,
  PLATFORM_TOUR_STEP_COMMUNITY,
  PLATFORM_TOUR_STEP_TRADES,
  PLATFORM_TOUR_STEP_PRICING,
];

/** Map step → target route. `/trades` is the sidebar nav label but the
 *  actual page at /trades/page.tsx is a redirect to /blog — so we
 *  route directly to /blog to skip the double-navigation flash.
 *  Keeping the STEP value as "trades" preserves the semantic name
 *  (so future URL reshuffles don't need a full rename migration). */
export const PLATFORM_TOUR_ROUTES: Record<PlatformTourStep, string> = {
  [PLATFORM_TOUR_STEP_AUTOMATIONS]: "/automations",
  [PLATFORM_TOUR_STEP_COMMUNITY]: "/community",
  [PLATFORM_TOUR_STEP_TRADES]: "/blog",
  [PLATFORM_TOUR_STEP_PRICING]: "/pricing",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Build a URL for a given step, e.g. `/automations?ptour=automations`.
 *  Used by the replay handoff + each scene's onComplete navigation. */
export function buildPlatformTourUrl(step: PlatformTourStep): string {
  return `${PLATFORM_TOUR_ROUTES[step]}?${PLATFORM_TOUR_QUERY_PARAM}=${step}`;
}

/** Returns the next step after `current`, or null if `current` is the
 *  final step. Used by each scene to wire its last-step onCta navigation. */
export function getNextPlatformTourStep(
  current: PlatformTourStep,
): PlatformTourStep | null {
  const idx = PLATFORM_TOUR_ORDER.indexOf(current);
  if (idx < 0 || idx >= PLATFORM_TOUR_ORDER.length - 1) return null;
  return PLATFORM_TOUR_ORDER[idx + 1];
}

/** Read the active platform-tour step from a searchParams object.
 *  Returns null if no step is active or the value is unrecognized
 *  (defensive — never trust URL params). */
export function readPlatformTourStep(
  searchParams: URLSearchParams | null | undefined,
): PlatformTourStep | null {
  if (!searchParams) return null;
  const raw = searchParams.get(PLATFORM_TOUR_QUERY_PARAM);
  if (!raw) return null;
  if ((PLATFORM_TOUR_ORDER as string[]).includes(raw)) {
    return raw as PlatformTourStep;
  }
  return null;
}
